/**
 * FirebaseEmailAdapter - Firebase Email/Password Authentication Adapter
 *
 * This adapter implements authentication via Firebase using email and password.
 * Users authenticated via this method follow the same flow as Microsoft login:
 * - Backend sets custom claims (role, organisationId)
 * - Redirect logic applies based on role (admin → admin app)
 *
 * @see https://firebase.google.com/docs/auth/web/password-auth
 */

import { type User, onAuthStateChanged, type Unsubscribe } from 'firebase/auth';
import {
  auth,
  signInWithEmailPassword,
  registerWithEmailPassword,
  signOut,
  getUserClaims,
  callSetClaimsEndpoint,
  refreshUserToken,
  UserNotAuthorizedError,
} from '../firebase';
import type {
  AuthAdapter,
  AuthUser,
  AuthProviderType,
  LoginOptions,
} from './AuthAdapter';
import { AuthError } from './AuthAdapter';

/**
 * FirebaseEmailAdapter
 *
 * Implements the AuthAdapter interface using Firebase Authentication
 * with email and password credentials.
 */
export class FirebaseEmailAdapter implements AuthAdapter {
  readonly providerType: AuthProviderType = 'firebase-email';

  private currentUser: User | null = null;
  private authStateUnsubscribe: Unsubscribe | null = null;

  constructor() {
    // Subscribe to auth state changes
    this.authStateUnsubscribe = onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
    });
  }

  /**
   * Clean up auth state listener
   */
  dispose(): void {
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
      this.authStateUnsubscribe = null;
    }
  }

  /**
   * Sign in with email and password
   */
  async login(options?: LoginOptions): Promise<void> {
    if (!options?.email || !options?.password) {
      throw new AuthError(
        'Email and password are required',
        'INVALID_CREDENTIALS',
        this.providerType
      );
    }

    try {
      const user = await signInWithEmailPassword(options.email, options.password);
      this.currentUser = user;

      // Always call backend to set/refresh claims and link external_id
      // The backend is idempotent - it will link external_id on first call
      // and just refresh claims on subsequent calls
      await callSetClaimsEndpoint(user);
      // Force token refresh to get the latest claims
      await refreshUserToken(user);
    } catch (error) {
      console.error('[FirebaseEmailAdapter] Login error:', error);
      
      // If user is not authorized, sign them out and throw specific error
      if (error instanceof UserNotAuthorizedError) {
        await signOut();
        this.currentUser = null;
        throw new AuthError(
          error.message,
          'USER_NOT_AUTHORIZED',
          this.providerType,
          error
        );
      }
      
      // Map Firebase error codes to user-friendly messages
      const firebaseError = error as { code?: string; message?: string };
      let message = 'Failed to sign in with email and password';
      let code = 'LOGIN_FAILED';

      if (firebaseError.code === 'auth/invalid-email') {
        message = 'Invalid email address';
        code = 'INVALID_EMAIL';
      } else if (firebaseError.code === 'auth/user-not-found') {
        message = 'No account found with this email address';
        code = 'USER_NOT_FOUND';
      } else if (firebaseError.code === 'auth/wrong-password' || firebaseError.code === 'auth/invalid-credential') {
        message = 'Incorrect email or password';
        code = 'INVALID_CREDENTIALS';
      } else if (firebaseError.code === 'auth/user-disabled') {
        message = 'This account has been disabled';
        code = 'USER_DISABLED';
      } else if (firebaseError.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later.';
        code = 'TOO_MANY_REQUESTS';
      }

      throw new AuthError(message, code, this.providerType, error);
    }
  }

  /**
   * Sign out the current user
   */
  async logout(): Promise<void> {
    try {
      await signOut();
      this.currentUser = null;
    } catch (error) {
      console.error('[FirebaseEmailAdapter] Logout error:', error);
      throw new AuthError(
        'Failed to sign out',
        'LOGOUT_FAILED',
        this.providerType,
        error
      );
    }
  }

  /**
   * Get the current authenticated user
   */
  async getUser(): Promise<AuthUser | null> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      const idToken = await user.getIdToken();

      return {
        id: user.uid,
        email: user.email || '',
        displayName: user.displayName || user.email || 'User',
        photoUrl: user.photoURL || undefined,
        idToken,
        accessToken: idToken,
        tokenExpiry: undefined,
      };
    } catch (error) {
      console.error('[FirebaseEmailAdapter] getUser error:', error);
      throw new AuthError(
        'Failed to get user information',
        'GET_USER_FAILED',
        this.providerType,
        error
      );
    }
  }

  /**
   * Get roles from custom claims
   */
  async getRoles(): Promise<string[]> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return [];
    }

    try {
      const claims = await getUserClaims(user);
      return claims.role ? [claims.role] : [];
    } catch (error) {
      console.error('[FirebaseEmailAdapter] getRoles error:', error);
      return [];
    }
  }

  /**
   * Check if a user is currently authenticated
   */
  isAuthenticated(): boolean {
    return !!(this.currentUser ?? auth.currentUser);
  }

  /**
   * Get a fresh ID token
   */
  async acquireTokenSilent(): Promise<string | null> {
    const user = this.currentUser ?? auth.currentUser;

    if (!user) {
      return null;
    }

    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('[FirebaseEmailAdapter] acquireTokenSilent error:', error);
      return null;
    }
  }

  /**
   * Register a new user with email and password
   * 
   * After registration, calls the backend to set claims.
   * If the backend doesn't recognise the user, an error is thrown
   * and the user cannot proceed.
   */
  async register(email: string, password: string): Promise<void> {
    try {
      const user = await registerWithEmailPassword(email, password);
      this.currentUser = user;

      // Call backend to set claims - this will fail if user isn't provisioned
      try {
        await callSetClaimsEndpoint(user);
        // Force token refresh to get the new claims
        await refreshUserToken(user);
      } catch (claimsError) {
        // User registered in Firebase but not in backend
        // Sign them out and throw an error
        console.error('[FirebaseEmailAdapter] User not provisioned in backend:', claimsError);
        await signOut();
        this.currentUser = null;
        throw new AuthError(
          'Your account has been created but is not yet activated. Please contact your administrator.',
          'USER_NOT_PROVISIONED',
          this.providerType,
          claimsError
        );
      }
    } catch (error) {
      // If it's already an AuthError, rethrow it
      if (error instanceof AuthError) {
        throw error;
      }

      console.error('[FirebaseEmailAdapter] Register error:', error);
      
      // Map Firebase error codes to user-friendly messages
      const firebaseError = error as { code?: string; message?: string };
      let message = 'Failed to create account';
      let code = 'REGISTER_FAILED';

      if (firebaseError.code === 'auth/email-already-in-use') {
        message = 'An account with this email already exists';
        code = 'EMAIL_IN_USE';
      } else if (firebaseError.code === 'auth/invalid-email') {
        message = 'Invalid email address';
        code = 'INVALID_EMAIL';
      } else if (firebaseError.code === 'auth/weak-password') {
        message = 'Password is too weak. Please use at least 6 characters.';
        code = 'WEAK_PASSWORD';
      } else if (firebaseError.code === 'auth/operation-not-allowed') {
        message = 'Email/password accounts are not enabled. Please contact support.';
        code = 'NOT_ALLOWED';
      }

      throw new AuthError(message, code, this.providerType, error);
    }
  }
}

// Export a singleton instance
export const firebaseEmailAdapter = new FirebaseEmailAdapter();
